(() => {
  const root = document.documentElement;
  const storageKey = 'wokiee-color-mode';
  let isTransitioning = false;
  let viewTransitionPrimed = false;

  const primeViewTransition = async () => {
    if (viewTransitionPrimed || !document.startViewTransition) return;
    viewTransitionPrimed = true;

    try {
      /* No visual animation is shown because the theme CSS disables the
         browser's default root transition. This only initializes the snapshot
         path immediately before the first real circular reveal. */
      const warm = document.startViewTransition(() => {});
      await warm.ready;
      await warm.finished;
    } catch (_) {}
  };

  const saveMode = (mode) => {
    try { localStorage.setItem(storageKey, mode); } catch (_) {}
  };

  const syncButtons = () => {
    const dark = root.dataset.theme === 'dark';
    document.querySelectorAll('.dark-mode-toggle').forEach((button) => {
      button.setAttribute('aria-pressed', dark ? 'true' : 'false');
      button.setAttribute('title', dark ? 'الوضع النهاري' : 'الوضع الليلي');
    });
  };

  const applyMode = (mode) => {
    if (mode === 'dark') root.dataset.theme = 'dark';
    else delete root.dataset.theme;
    root.style.colorScheme = mode === 'dark' ? 'dark' : 'light';
    syncButtons();
  };

  const getRevealGeometry = (button) => {
    /* Always anchor the reveal to the visual center of the actual toggle.
       Pointer coordinates vary depending on where inside the button was tapped,
       which made the circle appear to start from different places. */
    const rect = button.getBoundingClientRect();
    const x = Math.round(rect.left + rect.width / 2);
    const y = Math.round(rect.top + rect.height / 2);
    const farX = Math.max(x, innerWidth - x);
    const farY = Math.max(y, innerHeight - y);
    return { x, y, radius: Math.ceil(Math.hypot(farX, farY)) + 12 };
  };

  const animateButton = (button, nextMode) => {
    if (!button.animate) return;
    button.animate([
      { transform: 'scale(1) rotate(0deg)' },
      { transform: `scale(.82) rotate(${nextMode === 'dark' ? -18 : 18}deg)`, offset: .38 },
      { transform: 'scale(1.06) rotate(0deg)', offset: .76 },
      { transform: 'scale(1) rotate(0deg)' }
    ], {
      duration: 680,
      easing: 'cubic-bezier(.2,.8,.2,1)'
    });
  };

  const toggleMode = async (button, event) => {
    if (isTransitioning) return;


    const nextMode = root.dataset.theme === 'dark' ? 'light' : 'dark';
    const reduceMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (!document.startViewTransition || reduceMotion) {
      applyMode(nextMode);
      saveMode(nextMode);
      animateButton(button, nextMode);
      return;
    }

    isTransitioning = true;
    document.dispatchEvent(new CustomEvent('jsh:theme-transition-start'));
    const { x, y, radius } = getRevealGeometry(button);

    try {
      /* Prime only when the user requests the first toggle.
         No startup/idle work is added to normal page loading. */
      await primeViewTransition();

      const transition = document.startViewTransition(() => {
        applyMode(nextMode);
        saveMode(nextMode);
      });

      await transition.ready;
      animateButton(button, nextMode);

      /* V31: keep the same circular reveal, but avoid animating blur, brightness
         and drop-shadow on full-page snapshots. Those filters were the main
         source of jank on mobile GPUs. A slower clip-path + tiny opacity crossfade
         preserves the visual character while producing steadier frame pacing. */
      /* Stable circular reveal: animate only clip-path on the new root snapshot.
         Keep the old root snapshot static underneath. */
      const newSnapshot = root.animate([
        { clipPath: `circle(0px at ${x}px ${y}px)` },
        { clipPath: `circle(${radius}px at ${x}px ${y}px)` }
      ], {
        duration: 1360,
        easing: 'cubic-bezier(.18,.72,.16,1)',
        fill: 'both',
        pseudoElement: '::view-transition-new(root)'
      });

      await Promise.allSettled([newSnapshot.finished, transition.finished]);
    } catch (_) {
      applyMode(nextMode);
      saveMode(nextMode);
    } finally {
      isTransitioning = false;
      document.dispatchEvent(new CustomEvent('jsh:theme-transition-end'));
    }
  };

  document.addEventListener('click', (event) => {
    const button = event.target.closest('.dark-mode-toggle');
    if (!button) return;
    event.preventDefault();
    toggleMode(button, event);
  });

  document.addEventListener('shopify:section:load', syncButtons);
  document.addEventListener('DOMContentLoaded', syncButtons);
  syncButtons();
})();
